import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuditEvent } from '@magimp/shared';
import { useAuditQuery, type AuditQueryFilter } from '@magimp/shared-hooks';
import { Button, EmptyState, ErrorState, LoadingState } from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { AuditFiltersBar, type AuditFilterValue } from './AuditFiltersBar.js';
import { AuditTable } from './AuditTable.js';
import { AuditDetailSheet } from './AuditDetailSheet.js';

const EMPTY_FILTER: AuditFilterValue = {
  action: '',
  targetType: '',
  targetId: '',
  actorUserId: '',
  dateFrom: '',
  dateTo: '',
};

export function AuditPage() {
  const { t } = useTranslation(['audit', 'common']);
  const [filters, setFilters] = useState<AuditFilterValue>(EMPTY_FILTER);
  const [selected, setSelected] = useState<AuditEvent | null>(null);

  const queryFilter = useMemo<AuditQueryFilter>(() => {
    const out: AuditQueryFilter = {};
    if (filters.action) out.action = filters.action;
    if (filters.targetType) out.targetType = filters.targetType;
    if (filters.targetId) out.targetId = filters.targetId;
    if (filters.actorUserId) out.actorUserId = filters.actorUserId;
    if (filters.dateFrom) out.dateFrom = filters.dateFrom;
    if (filters.dateTo) out.dateTo = filters.dateTo;
    return out;
  }, [filters]);

  const query = useAuditQuery(queryFilter);

  const rows: AuditEvent[] = useMemo(
    () => (query.data?.pages ?? []).flatMap((p) => p.items),
    [query.data],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>{t('audit:title')}</h1>
      </header>
      <AuditFiltersBar value={filters} onChange={setFilters} />
      {query.isPending ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState text={errorMessage(query.error)} />
      ) : rows.length === 0 ? (
        <EmptyState title={t('audit:empty')} />
      ) : (
        <>
          <AuditTable rows={rows} onSelect={setSelected} />
          {query.hasNextPage ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
              <Button
                onClick={() => {
                  void query.fetchNextPage();
                }}
                disabled={query.isFetchingNextPage}
              >
                {query.isFetchingNextPage ? t('common:states.loading') : t('audit:loadMore')}
              </Button>
            </div>
          ) : null}
        </>
      )}
      {selected ? <AuditDetailSheet event={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}
